import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Sparkles,
  Building2,
  Users,
  Target,
  HelpCircle,
  CheckCircle2,
  Loader2,
  AlertCircle,
  ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import Button from '../components/ui/Button';
import Card, { CardHeader, CardBody } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import Spinner from '../components/ui/Spinner';

interface DiscoveryStatus {
  caseId: string;
  companyId: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  steps: {
    '1_company_data': string;
    '2_industry_research': string;
    '3_competition_analysis': string;
    '4_persona_generation': string;
  };
  data?: {
    industry?: {
      name: string;
      classification: string;
      marketSizeUSD: number;
      marketGrowthRate: number;
    };
    competitors?: {
      count: number;
      names: string[];
    };
    personas?: {
      count: number;
      roles: string[];
    };
    questions?: {
      total_count: number;
    };
  };
  error?: string;
}

export default function CaseDiscovery() {
  const { caseId } = useParams<{ caseId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [companyId, setCompanyId] = useState('');

  // Fetch discovery status
  const { data: status, isLoading: statusLoading, refetch } = useQuery<DiscoveryStatus>({
    queryKey: ['discovery-status', caseId],
    queryFn: async () => {
      const response = await api.get<DiscoveryStatus>(`/case-discovery/cases/${caseId}/status`);
      return response;
    },
    refetchInterval: (query) => {
      // Refetch every 5 seconds if processing
      return query.state.data?.status === 'processing' ? 5000 : false;
    },
    enabled: !!caseId,
  });

  // Start discovery mutation
  const startDiscovery = useMutation({
    mutationFn: async (payload: { companyId: string }) => {
      const response = await api.post<DiscoveryStatus>(
        `/case-discovery/cases/${caseId}/start`,
        payload,
        { headers: { 'X-User-Email': 'test@example.com' } }
      );
      return response;
    },
    onSuccess: () => {
      toast.success('Discovery started successfully!');
      refetch();
    },
    onError: (error: any) => {
      toast.error(`Discovery failed: ${error.message || 'Unknown error'}`);
    },
  });

  const getStepIcon = (stepStatus: string) => {
    switch (stepStatus) {
      case 'completed':
        return <CheckCircle2 className="w-5 h-5 text-green-500" />;
      case 'processing':
      case 'in_progress':
        return <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />;
      case 'failed':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      default:
        return <div className="w-5 h-5 rounded-full border-2 border-gray-300" />;
    }
  };

  const getStepLabel = (stepKey: string) => {
    const labels: Record<string, string> = {
      '1_company_data': 'Company Data',
      '2_industry_research': 'Industry Research',
      '3_competition_analysis': 'Competition Analysis',
      '4_persona_generation': 'Persona Generation',
    };
    return labels[stepKey] || stepKey;
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(value);
  };

  if (!caseId) {
    return (
      <div className="p-8">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">No Case Selected</h2>
          <p className="text-gray-600">Please select a case to start discovery.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-screen-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/dashboard')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Sparkles className="w-8 h-8 text-primary-500" />
              AI-Powered Case Discovery
            </h1>
            <p className="text-gray-600 mt-1">Case ID: {caseId}</p>
          </div>
        </div>
      </div>

      {statusLoading ? (
        <div className="flex justify-center items-center h-64">
          <Spinner size="lg" />
        </div>
      ) : (
        <div className="space-y-6">
          {/* Start Discovery Section */}
          {status?.status === 'pending' && (
            <Card>
              <CardHeader>
                <h2 className="text-xl font-semibold">Start Discovery</h2>
              </CardHeader>
              <CardBody>
                <p className="text-gray-600 mb-4">
                  Trigger AI-powered discovery to automatically generate industry research,
                  competitor analysis, and strategic personas with questions.
                </p>
                <div className="flex gap-4 items-end">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Company ID
                    </label>
                    <input
                      type="text"
                      value={companyId}
                      onChange={(e) => setCompanyId(e.target.value)}
                      placeholder="Enter company ID"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <Button
                    onClick={() => startDiscovery.mutate({ companyId })}
                    disabled={!companyId || startDiscovery.isPending}
                  >
                    {startDiscovery.isPending ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Starting...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Start Discovery
                      </>
                    )}
                  </Button>
                </div>
              </CardBody>
            </Card>
          )}

          {/* Progress Steps */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold">Discovery Progress</h2>
                <Badge variant={
                  status?.status === 'completed' ? 'success' :
                  status?.status === 'processing' ? 'warning' :
                  status?.status === 'failed' ? 'error' : 'default'
                }>
                  {status?.status || 'Pending'}
                </Badge>
              </div>
            </CardHeader>
            <CardBody>
              <div className="space-y-4">
                {status?.steps && Object.entries(status.steps).map(([key, value]) => (
                  <div key={key} className="flex items-center gap-4">
                    {getStepIcon(value)}
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">{getStepLabel(key)}</div>
                      <div className="text-sm text-gray-500 capitalize">{value}</div>
                    </div>
                  </div>
                ))}
              </div>
              {status?.error && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
                    <div>
                      <div className="font-medium text-red-900">Error</div>
                      <div className="text-sm text-red-700">{status.error}</div>
                    </div>
                  </div>
                </div>
              )}
            </CardBody>
          </Card>

          {/* Discovered Data */}
          {status?.data && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Industry */}
              {status.data.industry && (
                <Card>
                  <CardHeader>
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                      <Building2 className="w-5 h-5 text-primary-500" />
                      Industry Research
                    </h3>
                  </CardHeader>
                  <CardBody>
                    <div className="space-y-3">
                      <div>
                        <div className="text-sm font-medium text-gray-500">Name</div>
                        <div className="text-base font-semibold text-gray-900">
                          {status.data.industry.name}
                        </div>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-500">Classification</div>
                        <div className="text-base text-gray-900">
                          {status.data.industry.classification}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <div className="text-sm font-medium text-gray-500">Market Size</div>
                          <div className="text-lg font-bold text-primary-600">
                            {formatCurrency(status.data.industry.marketSizeUSD)}
                          </div>
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-500">Growth Rate</div>
                          <div className="text-lg font-bold text-green-600">
                            {status.data.industry.marketGrowthRate}%
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              )}

              {/* Competitors */}
              {status.data.competitors && (
                <Card>
                  <CardHeader>
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                      <Target className="w-5 h-5 text-primary-500" />
                      Competitors
                    </h3>
                  </CardHeader>
                  <CardBody>
                    <div className="space-y-3">
                      <div>
                        <div className="text-sm font-medium text-gray-500">Total Identified</div>
                        <div className="text-2xl font-bold text-gray-900">
                          {status.data.competitors.count}
                        </div>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-500 mb-2">Companies</div>
                        <div className="space-y-1">
                          {status.data.competitors.names.map((name, idx) => (
                            <div key={idx} className="text-sm text-gray-900 flex items-center gap-2">
                              <div className="w-1.5 h-1.5 bg-primary-500 rounded-full" />
                              {name}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              )}

              {/* Personas */}
              {status.data.personas && (
                <Card>
                  <CardHeader>
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                      <Users className="w-5 h-5 text-primary-500" />
                      Strategic Personas
                    </h3>
                  </CardHeader>
                  <CardBody>
                    <div className="space-y-3">
                      <div>
                        <div className="text-sm font-medium text-gray-500">Total Generated</div>
                        <div className="text-2xl font-bold text-gray-900">
                          {status.data.personas.count}
                        </div>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-500 mb-2">Roles</div>
                        <div className="flex flex-wrap gap-2">
                          {status.data.personas.roles.map((role, idx) => (
                            <Badge key={idx} variant="secondary">
                              {role}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              )}

              {/* Strategic Questions */}
              {status.data.questions && (
                <Card>
                  <CardHeader>
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                      <HelpCircle className="w-5 h-5 text-primary-500" />
                      Strategic Questions
                    </h3>
                  </CardHeader>
                  <CardBody>
                    <div className="space-y-3">
                      <div>
                        <div className="text-sm font-medium text-gray-500">Total Questions</div>
                        <div className="text-2xl font-bold text-gray-900">
                          {status.data.questions.total_count}
                        </div>
                      </div>
                      <div className="text-sm text-gray-600">
                        Strategic questions generated across all personas to drive analysis and hypothesis generation.
                      </div>
                    </div>
                  </CardBody>
                </Card>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
